import { ButtonTextNoBg } from 'components/Button'
import { CheckboxContainer, CheckmarkIcon, FilterLabel } from 'components/FilterRow'
import { SkeletonBase } from 'components/TopPools/styled'
import { useMemo } from 'react'
import { Text } from 'rebass'
import { LiquidityProvider } from 'state/providers/reducer'
import styled from 'styled-components'

import { ReactComponent as KodiakLogo } from '../../assets/svg/kodiak-logo-default.svg'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 12px;
`

const RowBetween = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const GridColumn = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`

const ProviderWrapper = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  cursor: pointer;
`

const RowMinGap = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const ImageContainer = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;

  img,
  svg {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`

const CustomButtonTextNoBg = styled(ButtonTextNoBg)`
  font-size: 14px !important;
  color: ${({ theme }) => theme.deprecated_text2} !important;
  min-width: max-content;
`

interface ProviderItemProps {
  provider: LiquidityProvider
  isActive: boolean
  onSelect: (providerId: string) => void
}

const ProviderItem = ({ provider, isActive, onSelect }: ProviderItemProps) => {
  const hasEmptyImageUrl = provider.imageUrl === ''

  const handleClick = () => {
    onSelect(provider.id)
  }

  return (
    <ProviderWrapper onClick={handleClick}>
      <CheckboxContainer>{isActive && <CheckmarkIcon size={10} />}</CheckboxContainer>
      <RowMinGap>
        <ImageContainer>
          {hasEmptyImageUrl ? (
            <KodiakLogo />
          ) : (
            <img src={typeof provider.imageUrl === 'string' ? provider.imageUrl : undefined} alt={provider.name} />
          )}
        </ImageContainer>
        <FilterLabel>{provider.name}</FilterLabel>
      </RowMinGap>
    </ProviderWrapper>
  )
}

interface LiquidityProvidersProps {
  isLoading: boolean
  availableList?: LiquidityProvider[]
  selectedList?: string[]
  onSelect?: (providerId: string) => void
  onReset?: () => void
  onSelectAll?: () => void
}

export default function LiquidityProviders({
  isLoading,
  availableList,
  selectedList,
  onSelect,
  onReset,
  onSelectAll,
}: LiquidityProvidersProps) {
  const isActive = (provider: LiquidityProvider) => {
    return (selectedList || []).includes(provider.id)
  }

  const handleProviderSelect = (providerId: string) => {
    if (onSelect) {
      onSelect(providerId)
    }
  }

  const { areAllProvidersSelected, buttonText } = useMemo(() => {
    const totalProvidersQty = availableList?.length ?? 0
    const selectedProvidersQty = selectedList?.length ?? 0

    const areAllSelected = !isLoading && totalProvidersQty > 0 && selectedProvidersQty === totalProvidersQty
    const buttonLabel = areAllSelected ? 'Clear' : 'Select all'

    return {
      areAllProvidersSelected: areAllSelected,
      buttonText: buttonLabel,
    }
  }, [availableList?.length, selectedList?.length, isLoading])

  const handleSelectionToggle = () => {
    if (areAllProvidersSelected) {
      onReset?.()
    } else {
      onSelectAll?.()
    }
  }

  return (
    <Wrapper>
      <RowBetween>
        <Text fontWeight={600} fontSize={14}>
          Liquidity Sources
        </Text>

        <CustomButtonTextNoBg onClick={handleSelectionToggle}>{buttonText}</CustomButtonTextNoBg>
      </RowBetween>

      <GridColumn>
        {isLoading &&
          Array.from({ length: 6 }).map((_, index) => (
            <SkeletonBase
              key={index}
              style={{
                height: '20px',
                width: '100px',
              }}
            />
          ))}
        {availableList &&
          !isLoading &&
          availableList.map((provider) => (
            <ProviderItem
              key={provider.id}
              provider={provider}
              isActive={isActive(provider)}
              onSelect={handleProviderSelect}
            />
          ))}
      </GridColumn>
    </Wrapper>
  )
}
